import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import CommonTable from '../../../common_components/CommonTable';
import { EXPAND_TRIGGERS } from '../../../common_components/tableConstants';
import TableToolbar from '../../../common_components/TableToolbar';
import AdPreview from '../../ClientAdApproval/components/AdPreview';
import { fetchAdPreview, fetchAdApprovalAccounts, selectClientAdApprovalAccounts } from '../../../redux/clientAdApprovalSlice';
import Pagination from '../../../common_components/Pagination';
import { toast } from '../../../common_components/Toast/ToastUtils';
const CHIP_STYLES = {
    pending: { bg: "#FEFCE8", color: "#936316" },
    approved: { bg: "#ECFDF3", color: "#027A48", label: "Approved" },
    rejected: { bg: "#FEF2F2", color: "#B42318", label: "Not Approved" },
};
const getChipConfig = (status, sentCount) => {
    const s = status?.toLowerCase?.() || "";
    if (s === "approved")
        return CHIP_STYLES.approved;
    if (s === "rejected" || s === "not_approved")
        return CHIP_STYLES.rejected;
    return { ...CHIP_STYLES.pending, label: sentCount > 0 ? `Waiting for Approval (${sentCount})` : "Waiting for Approval" };
};
const MetaAdStatusTab = ({ data, clientId }) => {
    const dispatch = useDispatch();
    const accountOptions = useSelector(selectClientAdApprovalAccounts);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [previewFormat, setPreviewFormat] = useState('MOBILE_FEED_STANDARD');
    const [previewData, setPreviewData] = useState({ loading: false, error: null, html: null, url: null });
    const [searchTerm, setSearchTerm] = useState("");
    const [sortConfig, setSortConfig] = useState(null);
    const [page, setPage] = useState(1);
    const limit = 20;
    React.useEffect(() => {
        setPage(1);
    }, [searchTerm, sortConfig]);
    const filteredData = useMemo(() => {
        if (!data)
            return [];
        if (!searchTerm.trim())
            return data;
        const lowerTerm = searchTerm.toLowerCase();
        return data.filter(row => {
            const name = (row.ad_name || row.name || "").toLowerCase();
            return name.includes(lowerTerm);
        });
    }, [data, searchTerm]);
    const sortedData = useMemo(() => {
        if (!sortConfig?.columnId)
            return filteredData;
        const { columnId, direction } = sortConfig;
        return [...filteredData].sort((a, b) => {
            const aVal = (a[columnId] ?? '').toString().toLowerCase();
            const bVal = (b[columnId] ?? '').toString().toLowerCase();
            if (aVal < bVal)
                return direction === 'asc' ? -1 : 1;
            if (aVal > bVal)
                return direction === 'asc' ? 1 : -1;
            return 0;
        });
    }, [filteredData, sortConfig]);
    const paginatedData = useMemo(() => {
        const startIndex = (page - 1) * limit;
        return sortedData.slice(startIndex, startIndex + limit);
    }, [sortedData, page, limit]);
    const previewFormatOptions = [
        { value: 'MOBILE_FEED_STANDARD', label: 'Mobile Feed Standard' },
        { value: 'DESKTOP_FEED_STANDARD', label: 'Desktop Feed Standard' }
    ];
    // ✅ Track last fetched ID to avoid refetching every time this tab remounts
    // for the same client (e.g. while the parent report reloads).
    const lastFetchedClientId = useRef(null);
    useEffect(() => {
        if (clientId && lastFetchedClientId.current !== clientId) {
            lastFetchedClientId.current = clientId;
            dispatch(fetchAdApprovalAccounts({ clientId }));
        }
    }, [clientId, dispatch]);
    const fetchPreview = async (row, format) => {
        if (!row?.adId) {
            toast.error("Preview not available: Missing ad ID.");
            return;
        }
        setPreviewData({ loading: true, error: null, html: null, url: null });
        try {
            const acc = accountOptions?.find((o) => o.value === row.connection_id);
            const accId = acc?.accountId || acc?.value || row.connection_id;
            const result = await dispatch(fetchAdPreview({
                adId: String(row.adId),
                format,
                accountId: String(accId),
                toastOnError: true
            })).unwrap();
            setPreviewData({
                loading: false,
                html: result?.html,
                url: result?.previewUrl,
                error: !result?.html && !result?.previewUrl ? "Preview unavailable" : null
            });
        }
        catch (e) {
            const errorMsg = typeof e === 'string' ? e : (e?.message || "Preview error");
            setPreviewData({ loading: false, html: null, url: null, error: errorMsg });
        }
    };
    const handlePreviewOpen = (row) => {
        setSelectedRow(row);
        setPreviewOpen(true);
        fetchPreview(row, previewFormat);
    };
    const handlePreviewFormatChange = (newFormat) => {
        setPreviewFormat(newFormat);
        if (selectedRow) {
            fetchPreview(selectedRow, newFormat);
        }
    };
    const columns = [
        { id: "ad_name", label: "Ad Name", sortable: true, minWidth: 260 },
        { id: "preview", label: "Preview", sortable: false, minWidth: 80, align: "center" },
        { id: "status", label: "Sent Status", sortable: true, minWidth: 160 },
    ];
    const renderCell = (row, column) => {
        if (column.id === "status") {
            const chip = getChipConfig(row.status, row.sent_count || 0);
            return (_jsx(Box, { sx: {
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    p: "4px 12px", borderRadius: "999px", fontSize: 12, fontWeight: 400,
                    minWidth: 150, textAlign: "center", backgroundColor: chip.bg, color: chip.color
                }, children: chip.label }));
        }
        if (column.id === "preview") {
            return (_jsx(Box, { component: "button", type: "button", sx: { border: "none", backgroundColor: "transparent", cursor: "pointer", p: 0 }, onClick: () => handlePreviewOpen(row), children: _jsx("img", { src: "/assets/eye.svg", alt: "preview" }) }));
        }
        return _jsx(Typography, { sx: { fontSize: '14px !important' }, children: row[column.id] || "—" });
    };
    if (!data || data.length === 0) {
        return _jsx(Typography, { sx: { p: 2, color: '#667085' }, children: "No Meta Ads data was found for the selected date range." });
    }
    return (_jsxs(Box, { sx: { width: '100%', border: '1px solid #EAECF0', borderRadius: '8px', overflow: 'hidden' }, children: [_jsx(TableToolbar, { title: "", showSearch: true, searchValue: searchTerm, onSearchChange: setSearchTerm, searchPlaceholder: "Search by ad name", showFullScreen: true }), _jsxs(Box, { className: "user-list-table-wrapper", children: [_jsx(CommonTable, { columns: columns, rows: paginatedData.map((r, i) => ({ ...r, id: r._id || i })), renderCell: renderCell, expandTrigger: EXPAND_TRIGGERS.NONE, sortConfig: sortConfig ? { columnId: sortConfig.columnId, direction: sortConfig.direction } : undefined, onSortChange: (id, dir) => setSortConfig(dir ? { columnId: id, direction: dir } : null), stickyHeader: true, maxHeight: 400 }), sortedData.length > 0 && (_jsx(Pagination, { currentPage: page, totalPages: Math.ceil(sortedData.length / limit), onPageChange: setPage, maxVisiblePages: 5, totalEntries: sortedData.length, itemsPerPage: limit }))] }), previewOpen && selectedRow && (_jsx(AdPreview, { open: previewOpen, onClose: () => {
                    setPreviewOpen(false);
                    setSelectedRow(null);
                }, previewRow: selectedRow, previewFormat: previewFormat, previewFormatOptions: previewFormatOptions, onPreviewFormatChange: handlePreviewFormatChange, loading: previewData.loading, error: previewData.error, html: previewData.html, previewUrl: previewData.url }))] }));
};
export default MetaAdStatusTab;
